import { useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import styled, { css } from 'styled-components'

import useLazyLoad from '../../hooks/useLazyLoad'
import theme from '../../utils/theme'
import { PrimaryButton } from '../Button'
import ListItemDraggable from '../ListItemDraggable'
import ListItemsWrapper from '../ListItemsWrapper'
import Skeleton from '../Skeleton'

const StyledSkeleton = styled.div`
  display: flex;
  flex-wrap: wrap;

  .skeleton {
    border-radius: 4px;
    margin: 0.5rem 1rem 0.5rem 0;
  }
`

const StyledLoader = styled.div`
  color: ${`var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})`};
  display: block;
  margin: 1rem 0;
  visibility: hidden;
  ${props =>
    props.loading &&
    css`
      visibility: visible;
    `}
`

const NoData = styled.div`
  color: ${`var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})`};
  width: '100%';
  margin: 2rem 0;
  height: '320px';
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
`

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const ListItemsEditor = ({
  buttonRenderer,
  children,
  customWidth,
  customWrapperWidth,
  draggable,
  firstLabelsOnly,
  fullWidth,
  handleAdd,
  handleDelete,
  isDeletable,
  isGrid,
  isImage,
  items,
  listItemRenderer,
  multipleDirection,
  onDragEnd = () => {},
  showProgress,
  topBorder,
  willDelete,
  onListItemsReordered,
  showRows,
  showNoData,
  disabled,
  ...rest
}) => {
  const perPage = showRows || 300
  const useLazy = items.length > perPage
  const dataLength = items.length
  const triggerRef = useRef(null)
  const loadData = currentPage => {
    return new Promise(resolve => {
      setTimeout(() => {
        const data = items.slice((currentPage - 1) * perPage, perPage * currentPage)
        resolve(data)
      }, 1500)
    })
  }
  const { data, loading } = useLazyLoad({ triggerRef, loadData, dataLength })
  const [inView, setInView] = useState(false)

  const thisOnDragEnd = result => {
    onDragEnd()
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const dragItems = reorder(items, result.source.index, result.destination.index)

    if (onListItemsReordered) {
      onListItemsReordered(dragItems)
    }
  }

  const ListItemsComponent = (useLazy ? data : items).map((item, index) => {
    const ListItemComponent = (
      <ListItemDraggable
        key={item.id}
        draggableId={`${item.id}`}
        index={index}
        handleDelete={() => handleDelete(item, index)}
        deletable={!disabled && isDeletable && isDeletable(item, index)}
        draggable={draggable}
        customWidth={customWidth}
        fullWidth={fullWidth}
        willDelete={item.action && item.action === 'delete' ? true : willDelete}
        isImage={isImage}
        multipleDirection={multipleDirection}
        showProgress={showProgress}
        isGrid={isGrid}
      >
        {listItemRenderer(item, index)}
      </ListItemDraggable>
    )

    return useLazy ? (
      // biome-ignore lint/correctness/useJsxKeyInIterable: TODO
      <InView onChange={setInView}>
        {/** biome-ignore lint/correctness/noUnusedFunctionParameters: TODO */}
        {({ inView, ref, entry }) => (
          <div ref={ref}>
            {inView ? (
              ListItemComponent
            ) : (
              <StyledSkeleton>
                <Skeleton width={customWidth - 30} height={36} variant="rect" className="skeleton" />
              </StyledSkeleton>
            )}
          </div>
        )}
      </InView>
    ) : (
      ListItemComponent
    )
  })

  const NoDataComponent = showNoData && <NoData>No Data Found</NoData>

  const noData = !useLazy && (items || []).length === 0

  return (
    <ListItemsWrapper
      draggable
      onDragEnd={thisOnDragEnd}
      buttonComponent={buttonRenderer ? buttonRenderer(items) : <PrimaryButton onClick={handleAdd}>Add</PrimaryButton>}
      topBorder={topBorder}
      firstLabelsOnly={firstLabelsOnly}
      multipleDirection={multipleDirection}
      customWrapperWidth={customWrapperWidth}
      isGrid={isGrid}
      inView={inView}
      {...rest}
    >
      <div>
        {noData ? NoDataComponent : ListItemsComponent}
        {children}
      </div>
      {useLazy && (
        <StyledLoader ref={triggerRef} loading={loading}>
          Loading...
        </StyledLoader>
      )}
    </ListItemsWrapper>
  )
}

export default ListItemsEditor
